import { getTelegram, tryGetTelegram } from './telegram';
import { DEV_PREVIEW } from './devPreview';
import { getMockResponse } from './devMocks';

const BACKEND_URL = (import.meta.env.VITE_BACKEND_URL ?? '/api').replace(/\/$/, '');

export interface AuthExchangeResponse {
  token: string;
  expiresAt: string;
  externalUserId: string;
  telegramUserId: string;
  userExists: boolean;
  walletAddress: string | null;
  depositWalletAddress: string | null;
  language: string;
  hasClobCreds: boolean;
}

export interface OrderDraftSummary {
  orderId: string;
  side: 'BUY' | 'SELL';
  outcomeLabel: string;
  marketQuestion: string | null;
  marketSlug: string | null;
  priceCents: number;
  size: number;
  totalUsd: number;
  potentialPayoutUsd: number;
  expiresAt: string;
}

export interface OrderParamsResponse {
  summary: OrderDraftSummary;
  typedData: {
    domain: Record<string, unknown>;
    types: Record<string, Array<{ name: string; type: string }>>;
    primaryType: string;
    message: Record<string, unknown>;
  };
}

let cachedToken: string | null = null;
let cachedTokenExp = 0;

const TOKEN_LS_KEY = 'torian.jwt';
const TOKEN_EXP_LS_KEY = 'torian.jwt.exp';

// Restore from previous session so a Mini App reload doesn't force a new
// initData exchange (Telegram regenerates initData on each open anyway, so
// the dev convenience matters mostly for hot-reload during development).
try {
  const tk = localStorage.getItem(TOKEN_LS_KEY);
  const exp = Number(localStorage.getItem(TOKEN_EXP_LS_KEY) ?? '0');
  if (tk && exp > Date.now() + 5_000) {
    cachedToken = tk;
    cachedTokenExp = exp;
  }
} catch {
  // localStorage may be unavailable (private mode); ignore.
}

export function setAuth(token: string, expiresAt: string): void {
  cachedToken = token;
  cachedTokenExp = new Date(expiresAt).getTime();
  try {
    localStorage.setItem(TOKEN_LS_KEY, token);
    localStorage.setItem(TOKEN_EXP_LS_KEY, String(cachedTokenExp));
  } catch {
    // ignore
  }
}

export function clearAuth(): void {
  cachedToken = null;
  cachedTokenExp = 0;
  try {
    localStorage.removeItem(TOKEN_LS_KEY);
    localStorage.removeItem(TOKEN_EXP_LS_KEY);
  } catch {
    // ignore
  }
}

export function getAuthToken(): string | null {
  if (DEV_PREVIEW) return 'dev-preview-token';
  if (!cachedToken) return null;
  if (Date.now() >= cachedTokenExp - 5_000) {
    clearAuth();
    return null;
  }
  return cachedToken;
}

/** Returns true if the cached token expires within `windowMs`. */
export function isTokenExpiringSoon(windowMs = 60_000): boolean {
  if (DEV_PREVIEW) return false;
  if (!cachedToken) return true;
  return Date.now() >= cachedTokenExp - windowMs;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: { auth?: boolean } = { auth: true },
): Promise<T> {
  if (DEV_PREVIEW) {
    // eslint-disable-next-line no-console
    console.debug(`[torian dev-preview] mock ${method} ${path}`);
    return getMockResponse(method, path) as T;
  }
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.auth !== false) {
    const token = getAuthToken();
    if (!token) throw new Error('Not authenticated. Reload the Mini App.');
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = (await res.json()) as { message?: string };
      if (j?.message) detail = j.message;
    } catch {
      // ignore
    }
    throw new Error(`${res.status} ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function exchangeAuth(walletAddress?: string, funderAddress?: string, signatureType?: number): Promise<AuthExchangeResponse> {
  if (DEV_PREVIEW) {
    return getMockResponse('POST', '/v1/webapp/auth/exchange') as AuthExchangeResponse;
  }
  const tg = tryGetTelegram();
  const initData = tg?.initData ?? '';
  if (!initData) {
    // Dev convenience: outside Telegram we'd never have initData. Throw early.
    throw new Error('Missing Telegram initData. Open inside Telegram.');
  }
  const data = await request<AuthExchangeResponse>(
    'POST',
    '/v1/webapp/auth/exchange',
    { initData, walletAddress, funderAddress, signatureType },
    { auth: false },
  );
  setAuth(data.token, data.expiresAt);
  return data;
}

export async function getOrderParams(orderId: string): Promise<OrderParamsResponse> {
  return request<OrderParamsResponse>('GET', `/v1/orders/${encodeURIComponent(orderId)}/params`);
}

export interface CreateOrderDraftInput {
  marketSlug: string;
  outcomeLabel: string;
  side: 'BUY' | 'SELL';
  amount: number;
  slippageCents?: number;
}

export async function createOrderDraft(
  input: CreateOrderDraftInput,
): Promise<OrderDraftSummary> {
  return request<OrderDraftSummary>('POST', '/v1/orders/draft', input);
}

export async function submitOrder(
  orderId: string,
  signature: string,
): Promise<{ id: string; status: string; errorMessage?: string | null }> {
  return request<{ id: string; status: string; errorMessage?: string | null }>(
    'POST',
    `/v1/orders/${encodeURIComponent(orderId)}/submit`,
    { signature },
  );
}

export async function uploadClobCreds(creds: {
  key: string;
  secret: string;
  passphrase: string;
}): Promise<void> {
  await request<{ ok: true }>('POST', '/v1/webapp/auth/clob-creds', creds);
}

export interface WalletBalanceResponse {
  address: string;
  pUsdBalance: string;
  isDeployed: boolean;
}

export async function getWalletBalance(
  externalUserId: string,
): Promise<WalletBalanceResponse> {
  return request<WalletBalanceResponse>(
    'GET',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/balance`,
  );
}

export interface WalletStatusResponse {
  address: string;
  isDeployed: boolean;
  ownerAddress: string;
  /** Deposit wallet already granted Polymarket allowances ("Enable trading"). */
  hasApprovals: boolean;
  deployedAt: string | null;
  txHash: string | null;
}

export async function getWalletStatus(
  externalUserId: string,
): Promise<WalletStatusResponse> {
  return request<WalletStatusResponse>(
    'GET',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet`,
  );
}

export type ForeignTokenSymbol = 'POL' | 'WMATIC' | 'USDT';

export interface ForeignTokenBalance {
  symbol: ForeignTokenSymbol;
  address: string | null;
  decimals: number;
  balance: string;
  rawBalance: string;
  hasBalance: boolean;
}

export interface ForeignBalancesResponse {
  address: string;
  hasAny: boolean;
  tokens: ForeignTokenBalance[];
}

export async function getForeignBalances(
  externalUserId: string,
): Promise<ForeignBalancesResponse> {
  return request<ForeignBalancesResponse>(
    'GET',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/foreign-balances`,
  );
}

export async function deployWallet(externalUserId: string): Promise<{
  address: string;
  transactionId: string;
  transactionHash: string;
  state: string;
}> {
  return request(
    'POST',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/deploy`,
  );
}

export interface SwapTypedDataResponse {
  typedData: {
    domain: Record<string, unknown>;
    types: Record<string, Array<{ name: string; type: string }>>;
    primaryType: string;
    message: Record<string, unknown>;
  };
  args: {
    from: string;
    chainId: number;
    walletAddress: string;
    factoryAddress: string;
    nonce: string;
    deadline: string;
    calls: Array<{ target: string; value: string; data: string }>;
  };
  amountIn: string;
  minOut: string;
}

export async function getSwapTypedData(
  externalUserId: string,
  body: { amount?: string; minOut?: string } = {},
): Promise<SwapTypedDataResponse> {
  return request<SwapTypedDataResponse>(
    'POST',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/swap-typed-data`,
    body,
  );
}

export interface SetupApprovalsTypedDataResponse {
  typedData: SwapTypedDataResponse['typedData'];
  args: SwapTypedDataResponse['args'];
}

export async function getSetupApprovalsTypedData(
  externalUserId: string,
): Promise<SetupApprovalsTypedDataResponse> {
  return request<SetupApprovalsTypedDataResponse>(
    'POST',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/setup-approvals-typed-data`,
    {},
  );
}

export type RescueMode = 'eoa' | 'pusd';

export interface RescueItem {
  symbol: ForeignTokenSymbol;
  address: string | null;
  decimals: number;
  rawBalance: string;
  balance: string;
}

export interface RescueToEoaTypedDataResponse {
  typedData: SwapTypedDataResponse['typedData'];
  args: SwapTypedDataResponse['args'];
  destination: string;
  items: RescueItem[];
}

export interface RescueToPusdTypedDataResponse {
  typedData: SwapTypedDataResponse['typedData'];
  args: SwapTypedDataResponse['args'];
  items: RescueItem[];
}

export async function getRescueToEoaTypedData(
  externalUserId: string,
): Promise<RescueToEoaTypedDataResponse> {
  return request<RescueToEoaTypedDataResponse>(
    'POST',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/rescue-to-eoa-typed-data`,
    {},
  );
}

export async function getRescueToPusdTypedData(
  externalUserId: string,
): Promise<RescueToPusdTypedDataResponse> {
  return request<RescueToPusdTypedDataResponse>(
    'POST',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/rescue-to-pusd-typed-data`,
    {},
  );
}

export interface BridgeAddressResponse {
  polymarketWallet: string;
  evm: string | null;
  svm: string | null;
  btc: string | null;
  tron: string | null;
  note?: string;
}

export async function getBridgeAddress(
  externalUserId: string,
): Promise<BridgeAddressResponse> {
  return request<BridgeAddressResponse>(
    'GET',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/bridge-address`,
  );
}

export interface TransferToBridgeTypedDataResponse {
  typedData: SwapTypedDataResponse['typedData'];
  args: SwapTypedDataResponse['args'];
  token: 'usdc' | 'usdce';
  tokenAddress: string;
  amount: string;
  bridgeAddress: string;
}

export async function getTransferToBridgeTypedData(
  externalUserId: string,
  body: { token?: 'usdc' | 'usdce'; amount?: string } = {},
): Promise<TransferToBridgeTypedDataResponse> {
  return request<TransferToBridgeTypedDataResponse>(
    'POST',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/transfer-to-bridge-typed-data`,
    body,
  );
}

export interface WithdrawStatusResponse {
  address: string;
  pUsdBalance: string;
  isDeployed: boolean;
  dailyLimitUsd: number;
  dailyUsedUsd: string;
  dailyRemainingUsd: string;
}

export async function getWithdrawStatus(
  externalUserId: string,
): Promise<WithdrawStatusResponse> {
  return request<WithdrawStatusResponse>(
    'GET',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/withdraw/status`,
  );
}

export interface WithdrawTypedDataResponse {
  withdrawalId: string;
  typedData: SwapTypedDataResponse['typedData'];
  args: SwapTypedDataResponse['args'];
  tokenAddress: string;
  amount: string;
  amountUsd: string;
  toAddress: string;
  dailyLimitUsd: number;
  dailyRemainingUsd: string;
}

export async function getWithdrawTypedData(
  externalUserId: string,
  body: { toAddress: string; amount: string },
): Promise<WithdrawTypedDataResponse> {
  return request<WithdrawTypedDataResponse>(
    'POST',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/withdraw-typed-data`,
    body,
  );
}

export async function relayWithdraw(
  externalUserId: string,
  body: {
    withdrawalId: string;
    type: string;
    from: string;
    to: string;
    nonce: string;
    signature: string;
    depositWalletParams: {
      depositWallet: string;
      deadline: string;
      calls: Array<{ target: string; value: string; data: string }>;
    };
  },
): Promise<{
  withdrawalId: string;
  transactionId: string;
  transactionHash: string;
  state: string;
}> {
  return request(
    'POST',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/withdraw-relay`,
    body,
  );
}

export async function forwardWalletBatch(
  externalUserId: string,
  body: {
    type: string;
    from: string;
    to: string;
    nonce: string;
    signature: string;
    depositWalletParams: {
      depositWallet: string;
      deadline: string;
      calls: Array<{ target: string; value: string; data: string }>;
    };
  },
): Promise<{ transactionId: string; transactionHash: string; state: string }> {
  return request(
    'POST',
    `/v1/users/${encodeURIComponent(externalUserId)}/wallet/batch`,
    body,
  );
}

/**
 * Re-issues a fresh JWT using the current bearer. Returns the same shape as
 * `exchangeAuth` so callers can swap them transparently.
 */
export async function refreshAuth(): Promise<AuthExchangeResponse> {
  const data = await request<AuthExchangeResponse>('POST', '/v1/webapp/auth/refresh');
  setAuth(data.token, data.expiresAt);
  return data;
}

export interface MarketOutcomeSummary {
  label: string;
  price: number;
  tokenId: string;
}

export interface MarketSummary {
  id: string;
  slug: string;
  question: string;
  conditionId: string;
  category: string | null;
  image: string | null;
  icon: string | null;
  endDate: string | null;
  active: boolean;
  closed: boolean;
  volume24hr: number | null;
  liquidity: number | null;
  outcomes: MarketOutcomeSummary[];
  lastTradePrice: number | null;
}

export interface ListMarketsParams {
  search?: string;
  tag_slug?: string;
  limit?: number;
  offset?: number;
  order?: 'volume24hr' | 'liquidity' | 'endDate' | 'startDate';
  ascending?: boolean;
}

/** Public read endpoint — no auth required. */
export async function listMarkets(
  params: ListMarketsParams,
): Promise<MarketSummary[]> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.toString();
  return request<MarketSummary[]>(
    'GET',
    `/v1/markets${suffix ? `?${suffix}` : ''}`,
    undefined,
    { auth: false },
  );
}

export interface MarketDetail extends MarketSummary {
  description: string | null;
  startDate: string | null;
  bestBid: number | null;
  bestAsk: number | null;
  orderPriceMinTickSize: number | null;
  orderMinSize: number | null;
  negRisk: boolean;
  volume: number | null;
  volume1wk: number | null;
  volume1mo: number | null;
  oneDayPriceChange: number | null;
  oneHourPriceChange: number | null;
}

/** Public read endpoint — no auth required. */
export async function getMarketDetail(slug: string): Promise<MarketDetail> {
  return request<MarketDetail>(
    'GET',
    `/v1/markets/${encodeURIComponent(slug)}`,
    undefined,
    { auth: false },
  );
}

export interface PositionSummary {
  proxyWallet: string;
  asset: string;
  conditionId: string;
  marketSlug: string;
  eventSlug: string | null;
  title: string;
  icon: string | null;
  outcome: string;
  outcomeIndex: number;
  oppositeOutcome: string | null;
  oppositeAsset: string | null;
  size: number;
  avgPrice: number;
  curPrice: number;
  initialValue: number;
  currentValue: number;
  cashPnl: number;
  percentPnl: number;
  realizedPnl: number;
  percentRealizedPnl: number;
  totalBought: number;
  redeemable: boolean;
  mergeable: boolean;
  endDate: string | null;
  negativeRisk: boolean;
}

export interface ListPositionsParams {
  sizeThreshold?: number;
  redeemable?: boolean;
  mergeable?: boolean;
  limit?: number;
  offset?: number;
  sortBy?:
    | 'CURRENT'
    | 'INITIAL'
    | 'TOKENS'
    | 'CASHPNL'
    | 'PERCENTPNL'
    | 'TITLE'
    | 'RESOLVING'
    | 'PRICE'
    | 'AVGPRICE';
}

/** Public read endpoint — no auth required. */
export async function listPositions(
  externalUserId: string,
  params: ListPositionsParams = {},
): Promise<PositionSummary[]> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.toString();
  return request<PositionSummary[]>(
    'GET',
    `/v1/users/${encodeURIComponent(externalUserId)}/positions${
      suffix ? `?${suffix}` : ''
    }`,
    undefined,
    { auth: false },
  );
}

// ===== Orders =====

export type OrderStatus =
  | 'PENDING'
  | 'OPEN'
  | 'MATCHED'
  | 'CANCELLED'
  | 'FAILED';

export interface OrderSummary {
  id: string;
  /** Present on single-order responses (POST /v1/orders/*, GET/DELETE /v1/orders/:id). */
  userId?: string;
  clobOrderId: string | null;
  tokenId: string;
  marketSlug: string | null;
  conditionId: string | null;
  side: 'BUY' | 'SELL';
  orderType: 'GTC' | 'GTD' | 'FOK' | 'FAK';
  /** Decimal price (0..1) as string. */
  price: string;
  /** Decimal size/notional as string. */
  size: string;
  expiration: number | null;
  /** Present on single-order responses; omitted in enriched list. */
  builderCode?: string;
  status: OrderStatus;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  /** Enriched via Gamma (best-effort; can be null if upstream fails). */
  marketQuestion?: string | null;
  /** Enriched via Gamma — label do outcome do `tokenId` (ex.: "Yes"). */
  outcome?: string | null;
  /** Enriched via Gamma — `https://polymarket.com/event/<eventSlug>`. */
  marketUrl?: string | null;
}

export interface ListOrdersParams {
  status?: OrderStatus;
  limit?: number;
}

export async function listOrders(
  params: ListOrdersParams = {},
): Promise<OrderSummary[]> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.toString();
  return request<OrderSummary[]>(
    'GET',
    `/v1/orders${suffix ? `?${suffix}` : ''}`,
  );
}

export async function cancelOrder(orderId: string): Promise<OrderSummary> {
  return request<OrderSummary>(
    'DELETE',
    `/v1/orders/${encodeURIComponent(orderId)}`,
  );
}

// ----- leaderboard -----

export type LeaderboardPeriod = 'day' | 'week' | 'month' | 'all';

export interface LeaderboardEntry {
  rank: number;
  externalUserId: string;
  telegramUsername: string | null;
  volumeUsd: number;
  tradeCount: number;
}

export async function getLeaderboard(
  period: LeaderboardPeriod = 'week',
  limit = 10,
): Promise<LeaderboardEntry[]> {
  const qs = new URLSearchParams({ period, limit: String(limit) });
  return request<LeaderboardEntry[]>('GET', `/v1/leaderboard?${qs.toString()}`);
}

// ----- copy trading -----

export interface CopyFollowLeaderMeta {
  address: string;
  pseudonym: string | null;
  name: string | null;
  profileImage: string | null;
  volumeUsd30d: number;
  pnlUsd30d: number | null;
  rank: number | null;
}

export interface CopyFollow {
  followerUserId: string;
  leaderAddress: string;
  sizeMultiplier: number;
  maxUsdPerTrade: number;
  active: boolean;
  createdAt: string;
  leader?: CopyFollowLeaderMeta | null;
}

export interface AddCopyFollowInput {
  leaderAddress: string;
  sizeMultiplier?: number;
  maxUsdPerTrade?: number;
  /** Optional leader metadata so the follow list can show name/avatar. */
  pseudonym?: string;
  name?: string;
  profileImage?: string;
}

export async function listCopyFollows(): Promise<CopyFollow[]> {
  return request<CopyFollow[]>('GET', '/v1/copy');
}

export async function addCopyFollow(
  input: AddCopyFollowInput,
): Promise<CopyFollow> {
  return request<CopyFollow>('POST', '/v1/copy/follow', input);
}

export async function removeCopyFollow(leaderAddress: string): Promise<void> {
  await request<void>(
    'DELETE',
    `/v1/copy/${encodeURIComponent(leaderAddress)}`,
  );
}

// ----- copy trading: discover (top traders) -----

export interface CopyLeader {
  address: string;
  pseudonym: string | null;
  name: string | null;
  profileImage: string | null;
  volumeUsd: number;
  pnlUsd: number | null;
  rank: number | null;
}

export interface CopyLeaderTrade {
  market: string;
  slug: string;
  outcome: string;
  side: 'BUY' | 'SELL';
  price: number;
  sizeUsd: number;
  /** Unix seconds. */
  timestamp: number;
}

export interface CopyLeaderDetail extends Omit<CopyLeader, 'volumeUsd'> {
  volumeUsd: number | null;
  recentTrades: CopyLeaderTrade[];
}

export async function listCopyLeaders(
  period: '1d' | '7d' | '30d' | 'all' = '30d',
  limit = 50,
): Promise<CopyLeader[]> {
  const qs = new URLSearchParams({ period, limit: String(limit) });
  return request<CopyLeader[]>('GET', `/v1/copy/leaders?${qs.toString()}`);
}

export async function getCopyLeader(
  address: string,
): Promise<CopyLeaderDetail> {
  return request<CopyLeaderDetail>(
    'GET',
    `/v1/copy/leaders/${encodeURIComponent(address)}`,
  );
}

// ----- copy trading: notifications (copy signals) -----

export interface CopyNotification {
  id: string;
  status: string;
  suggestedUsd: number;
  leaderAddress: string;
  leaderLabel: string;
  marketSlug: string;
  marketQuestion: string;
  outcome: string;
  side: 'BUY' | 'SELL';
  price: number;
  matchTime: string;
}

export interface CopyExecuteIntent {
  slug: string;
  outcome: string;
  side: 'BUY' | 'SELL';
  amount: number;
}

export async function getCopyNotification(
  id: string,
): Promise<CopyNotification> {
  return request<CopyNotification>(
    'GET',
    `/v1/copy/notifications/${encodeURIComponent(id)}`,
  );
}

export async function executeCopyNotification(
  id: string,
  amountUsd?: number,
): Promise<CopyExecuteIntent> {
  return request<CopyExecuteIntent>(
    'POST',
    `/v1/copy/notifications/${encodeURIComponent(id)}/execute`,
    amountUsd != null ? { amountUsd } : {},
  );
}

export async function dismissCopyNotification(id: string): Promise<void> {
  await request<void>(
    'POST',
    `/v1/copy/notifications/${encodeURIComponent(id)}/dismiss`,
  );
}

export function ensureTelegramReady(): void {
  if (DEV_PREVIEW) return;
  const tg = getTelegram();
  tg.ready();
  tg.expand();
}
